import { useEffect, useState } from 'react';
import {
  useFormik,
} from 'formik';
import * as yup from 'yup';
import {
  IconButton,
  Button,
  TextField,
  FormControl,
  Grid,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Close,
  Create,
  CalendarToday,
  ColorLens,
} from '@mui/icons-material';
import { AppointmentModel } from '@devexpress/dx-react-scheduler';
import {
  AppointmentForm,
} from '@devexpress/dx-react-scheduler-material-ui';
import { Color, ColorPicker, createColor } from 'material-ui-color';
import './index.scss'
import { classes, StyledDiv } from '../config';

const AppointmentFormContainer = (props: any) => {
  const {
    visible,
    visibleChange,
    appointmentData,
    cancelAppointment,
    target,
    onHide,
    commitChanges,
  } = props;

  const [appointmentChanges, setAppointmentChanges] = useState<any>({});
  const [isNewAppointment] = useState(appointmentData.id === undefined);
  const todayDate = new Date()
  const startDate = new Date(appointmentData.startDate)
  let endDateAux = new Date()

  if (typeof appointmentData.endDate === 'number') {
    endDateAux = new Date(todayDate.setHours(0 + 23, 59, 0)) //when select default date, add one day
  } else {
    if (appointmentData.allDay) {
      endDateAux = new Date(startDate.setHours(0 + 23, 59, 0))
    } else {
      endDateAux = new Date(appointmentData.endDate) // when dateEnds changes
    }
  }

  const initialValues: AppointmentModel = {
    title: appointmentData.title,
    startDate: new Date(appointmentData.startDate),
    endDate: endDateAux,
    color: appointmentData.color || '#feb100',
  };

  const NewProjectSchema = yup.object().shape({
    title: yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    startDate: yup.date()
      .nullable(),
    endDate: yup.date()
      .min(yup.ref('startDate'))
      .nullable(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: NewProjectSchema,
    onSubmit: async (variables) => {
      visibleChange();
      applyChanges();
    },
  });

  const changeAppointment = ({ field, changes }: any) => {
    const nextChanges = {
      ...appointmentChanges,
      [field]: changes,
    };
    setAppointmentChanges(nextChanges);
  };

  const commitAppointment = (type: any) => {
    const appointment = {
      ...appointmentData,
      ...appointmentChanges,
    };
    if (type === 'deleted') {
      commitChanges({ [type]: appointment.id });
    } else if (type === 'changed') {
      commitChanges({ [type]: { [appointment.id]: appointmentChanges } });
    } else {
      commitChanges({ [type]: appointment });
    }
    setAppointmentChanges({});
  };

  const cancelChanges = () => {
    setAppointmentChanges({});
    visibleChange();
    cancelAppointment();
  };

  const changeValues = (type: string, newValue: any) => {
    formik.setFieldValue(type, newValue);
    changeAppointment({
      field: [type],
      changes: newValue,
    })
  }

  const handleChange = (newValue: Color) => {
    changeValues('color', `#${newValue.hex}`);
  };

  const applyChanges = isNewAppointment
    ? () => commitAppointment('added')
    : () => commitAppointment('changed');

  return (
    <AppointmentForm.Overlay
      visible={visible}
      target={target}
      fullSize={false}
      onHide={onHide}
      className='test'
    >
      <StyledDiv className='styleDiv'>
        <div className='headerCalendar'>
          <IconButton className='closeButton' onClick={cancelChanges} size="large">
            <Close color="action" />
          </IconButton>
        </div>
        <FormControl
          fullWidth
          component="form"
          onSubmit={formik.handleSubmit}
        >
          <div className='content'>
            <div className='wrapper'>
              <Create className='icon' color="action" />
              <TextField
                required
                className='textField'
                id="title"
                label="Title"
                name="title"
                autoComplete="title"
                autoFocus
                value={formik.values.title}
                onChange={(event) => {
                  changeValues('title', event.target.value);
                }}
                error={formik.touched.title && Boolean(formik.errors.title)}
              />
            </div>
            <div className='wrapper'>
              <CalendarToday className='icon' color="action" />
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
              >
                <DatePicker
                  label="Start Date"
                  value={formik.values.startDate}
                  onError={() => null}
                  onChange={(value) => {
                    changeValues('startDate', value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      sx={{
                        mr: 2,
                      }}
                      id="startDate"
                      name="startDate"
                      className='dateField'
                      fullWidth
                      autoComplete="startDate"
                      error={formik.touched.start_date && Boolean(formik.errors.start_date)}
                      helperText={formik.touched.start_date && formik.errors.start_date}
                      {...params}
                    />
                  )}
                />
              </LocalizationProvider>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
              >
                <DatePicker
                  label="End Date"
                  value={formik.values.endDate}
                  onError={() => null}
                  onChange={(value) => {
                    changeValues('endDate', value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      id="endDate"
                      name="endDate"
                      fullWidth
                      autoComplete="endDate"
                      error={formik.touched.end_date && Boolean(formik.errors.end_date)}
                      helperText={formik.touched.end_date && formik.errors.end_date}
                      {...params}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>
            <div className='wrapperButtons'>
              <div className='wrapperColor'>
                <ColorLens className='icon' color="action" />
                <ColorPicker value={formik.values.color} onChange={handleChange} hideTextfield disableAlpha />
              </div>
              <div className='buttonGroup'>
                {!isNewAppointment && (
                  <Button
                    variant="contained"
                    color="secondary"
                    className='button'
                    onClick={() => {
                      visibleChange();
                      commitAppointment('deleted');
                    }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className='button'
                  disabled={!(formik.isValid && formik.dirty)}
                >
                  {isNewAppointment ? 'Create' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </FormControl>
      </StyledDiv>
    </AppointmentForm.Overlay >
  );
}

export default AppointmentFormContainer;
